/**
 * "This weekend" is Saturday 00:00 through Sunday night in America/Los_Angeles.
 * It is not a rolling `windowDays`. The page asks for enough days to reach
 * Monday 00:00 Pacific, then hides events outside that weekend.
 */

const ZONE = 'America/Los_Angeles';

export interface WeekendRange {
  /** Saturday 00:00 Pacific. */
  start: Date;
  /** Monday 00:00 Pacific, the end of Sunday night. */
  end: Date;
}

export function weekendRange(now: Date): WeekendRange {
  const today = pacificYmd(now);
  const weekday = pacificWeekday(now);
  const saturday =
    weekday === 'Sat'
      ? today
      : weekday === 'Sun'
        ? addDays(today, -1)
        : addDays(today, daysUntilSaturday(weekday));
  return { start: pacificMidnight(saturday), end: pacificMidnight(addDays(saturday, 2)) };
}

/** Whole days to request so the API window reaches the end of this weekend. */
export function weekendFetchDays(now: Date): number {
  const remaining = weekendRange(now).end.getTime() - now.getTime();
  if (remaining <= 0) return 1;
  return Math.min(14, Math.max(1, Math.ceil(remaining / 86_400_000)));
}

export function overlapsWeekend(
  startInstant: string,
  endInstant: string,
  range: WeekendRange,
): boolean {
  const start = Date.parse(startInstant);
  const end = Date.parse(endInstant);
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return end > range.start.getTime() && start < range.end.getTime();
}

function daysUntilSaturday(weekday: string): number {
  switch (weekday) {
    case 'Mon':
      return 5;
    case 'Tue':
      return 4;
    case 'Wed':
      return 3;
    case 'Thu':
      return 2;
    case 'Fri':
      return 1;
    default:
      return 6;
  }
}

function pacificWeekday(now: Date): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: ZONE, weekday: 'short' }).format(now);
}

function pacificYmd(now: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

function addDays(ymd: string, days: number): string {
  const [year, month, day] = ymd.split('-').map(Number);
  const shifted = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days));
  const y = shifted.getUTCFullYear();
  const m = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const d = String(shifted.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** UTC instant of midnight on a Pacific calendar day. */
function pacificMidnight(ymd: string): Date {
  const [year, month, day] = ymd.split('-').map(Number);
  const localMidnight = Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1);
  let utc = localMidnight - zoneOffsetMs(new Date(localMidnight));
  utc = localMidnight - zoneOffsetMs(new Date(utc));
  return new Date(utc);
}

function zoneOffsetMs(instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const value = (type: string): number =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour') % 24,
    value('minute'),
    value('second'),
  );
  return asUtc - instant.getTime();
}
